import { useNavigate, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { api, type AuthUser } from '../lib/api';
import { useAuth } from '../lib/auth';
import { BynderVideo } from '../lib/BynderVideo';

/**
 * Guest landing — mirrors legacy `resources/views/guest.blade.php`.
 * Two-column layout: Welcome + CTA on the left, intro video on the right.
 */
export function LandingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [videoOpen, setVideoOpen] = useState(false);
  const [tosOpen, setTosOpen] = useState(false);

  // Set by RequireAuth when an unauthenticated visitor follows a protected
  // deep-link (e.g. the "Edit Structure" / "Download Structure" email buttons).
  // Preserved so register/login can return the visitor to their destination.
  const from = (location.state as { from?: string } | null)?.from ?? null;

  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterBusy, setNewsletterBusy] = useState(false);
  const [newsletterMsg, setNewsletterMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const submitNewsletter = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newsletterEmail) return;
    setNewsletterBusy(true);
    setNewsletterMsg(null);
    try {
      const res = await api.post<{ success: boolean; detail: string | null }>(
        '/api/v1/newsletter',
        { email: newsletterEmail },
      );
      if (res.success) {
        setNewsletterMsg({ ok: true, text: 'Thanks! You\u2019ve been added to the list.' });
        setNewsletterEmail('');
      } else {
        setNewsletterMsg({ ok: false, text: res.detail ?? 'Signup failed. Please try again later.' });
      }
    } catch (err) {
      setNewsletterMsg({
        ok: false,
        text: err instanceof Error ? err.message : 'Signup failed. Please try again later.',
      });
    } finally {
      setNewsletterBusy(false);
    }
  };

  const startGuest = async () => {
    setBusy(true);
    setError(null);
    try {
      const me = await api.post<AuthUser>('/api/v1/auth/guest', {});
      setUser(me);
      navigate('/newstructure');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start guest session');
      setBusy(false);
    }
  };

  return (
    <div className="d-flex flex-column min-vh-100">
      {/* Topbar */}
      <div className="navbar-custom topnav-navbar shadow-sm mb-1 clearfix">
        <div className="container-fluid d-none d-lg-block">
          <a className="topnav-logo" href="/">
            <span className="topnav-logo-lg">
              <img src="/attribute/ads.png" alt="" height={70} />
            </span>
          </a>
          <a className="topnav-text" href="/">
            <span>FlexStorm Project Planner</span>
          </a>
        </div>
        <div className="container-fluid d-flex d-lg-none justify-content-between">
          <div style={{ width: 65 }}>&nbsp;</div>
          <a className="topnav-logo" href="/">
            <img src="/attribute/ads.png" alt="" className="img-fluid" style={{ maxHeight: '5vh', minHeight: 70 }} />
          </a>
          <div style={{ width: 65 }}>&nbsp;</div>
        </div>
      </div>

      <div className="container-fluid flex-fill">
        <div className="row mt-lg-5 mt-3 justify-content-center">
          <div className="col-lg-6 col-12">
            <div className="row">
              <div className="col-12">
                <h1 className="text-center">
                  Welcome to <span style={{ color: '#73a300' }}>the FlexStorm Project Planner</span>
                </h1>
                <p className="fs-3 text-center">
                  Quickly identify and save part numbers to build your next FlexStorm project.
                </p>
              </div>
            </div>

            <div className="row">
              <div className="col-xxl-4 offset-xxl-2 col-lg-5 offset-lg-1 my-2 my-lg-3">
                <div className="d-grid">
                  <button
                    type="button"
                    onClick={() => navigate('/register', from ? { state: { from } } : undefined)}
                    className="btn btn-primary btn-lg platformtraffic"
                  >
                    <div>Create Account</div>
                  </button>
                </div>
              </div>
              <div className="col-xxl-4 col-lg-5 my-2 my-lg-3">
                <div className="d-grid">
                  <button
                    type="button"
                    onClick={() => navigate('/login', from ? { state: { from } } : undefined)}
                    className="btn btn-secondary btn-lg platformtraffic"
                  >
                    <div>Log In</div>
                  </button>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-xxl-8 offset-xxl-2 col-lg-10 offset-lg-1 my-2">
                <p className="fs-6 text-center">
                  Don't have time to setup a free account? Try the tool in guest mode.
                  An account will be required to save and share your&nbsp;project.
                </p>
              </div>
            </div>

            <div className="row">
              <div className="col-xxl-8 col-lg-10 mx-auto">
                <div className="d-grid">
                  <button
                    type="button"
                    onClick={startGuest}
                    disabled={busy}
                    className="btn btn-primary btn-lg platformtraffic"
                  >
                    <div>{busy ? 'Starting…' : 'Use Tool As Guest'}</div>
                  </button>
                </div>
                {error && (
                  <p className="text-danger text-center mt-2 mb-0">{error}</p>
                )}
              </div>
            </div>
          </div>

          <div className="col-lg-5 col-12 d-grid justify-content-center">
            <hr className="d-block d-lg-none" />
            <p className="fs-3 text-center">
              See How the ADS FlexStorm Project Planner Works
            </p>
            <a
              className="platformtraffic"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setVideoOpen(true);
              }}
            >
              <img className="img-fluid" src="/images/Image-Video.png" alt="Play intro video" />
            </a>
          </div>
        </div>

        {/* Join Waiting List — mirrors legacy `<x-emailfooterform />`. */}
        <div className="row border-top mt-5 pt-4 justify-content-center align-items-center">
          <div className="col-12 col-md-6 col-xl-4 text-center">
            <p className="fs-3 text-center mb-0">Get new product info and more in advance.</p>
          </div>
          <div className="col-12 col-md-6 col-xl-4 text-center mt-3 mt-md-0">
            <form onSubmit={submitNewsletter} className="d-inline-block w-100">
              <div className="input-group">
                <input
                  type="email"
                  className="form-control"
                  placeholder="Email Address"
                  name="email"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  required
                  disabled={newsletterBusy}
                  aria-label="Email Address"
                />
                <button
                  type="submit"
                  className="btn btn-secondary fw-bold"
                  disabled={newsletterBusy}
                >
                  {newsletterBusy ? 'Joining\u2026' : 'Join The List'}
                </button>
              </div>
              {newsletterMsg && (
                <p
                  className={`mt-2 mb-0 small ${newsletterMsg.ok ? 'text-success' : 'text-danger'}`}
                  role="status"
                >
                  {newsletterMsg.text}
                </p>
              )}
            </form>
          </div>
        </div>
      </div>

      {videoOpen && (
        <div
          className="modal fade show d-block"
          style={{ background: 'rgba(0,0,0,0.7)' }}
          role="dialog"
          tabIndex={-1}
          onClick={(e) => {
            if (e.target === e.currentTarget) setVideoOpen(false);
          }}
        >
          <div className="modal-dialog modal-fullscreen">
            <div className="modal-content">
              <div className="modal-header">
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={() => setVideoOpen(false)}
                />
              </div>
              <div className="modal-body p-0">
                <BynderVideo
                  mediaId="53B94EFC-F714-4F97-97AB902903859A2E"
                  style={{ width: '100%', height: '100%' }}
                />
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={() => setVideoOpen(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <footer className="footer footer-alt mt-auto">
        <div className="container-fluid">
          <div className="text-md-center footer-links d-block">
            <span className="lh-lg me-0 me-lg-4">
            &copy; {new Date().getFullYear()} Advanced Drainage Systems&trade;. All&nbsp;rights&nbsp;reserved.
            </span>
            <br className="d-lg-none" />
            <a className="lh-lg py-1 ms-0" target="_blank" rel="noreferrer" href="https://www.adspipe.com/legal">Legal</a>
            <span className="d-lg-inline d-none">&nbsp;|&nbsp;</span>
            <br className="d-lg-none" />
            <a
              className="lh-lg py-1 ms-0"
              href="#"
              onClick={(event) => {
                event.preventDefault();
                setTosOpen(true);
              }}
            >
              Terms&nbsp;and&nbsp;Conditions
            </a>
            <span className="d-md-inline d-none">&nbsp;|&nbsp;</span>
            <br className="d-lg-none" />
            <a className="lh-lg py-1 ms-0" target="_blank" rel="noreferrer" href="https://www.adspipe.com/privacy-policy">Privacy&nbsp;Policy</a>
            <span className="d-md-inline d-none">&nbsp;|&nbsp;</span>
            <br className="d-lg-none" />
            <a
              className="lh-lg py-1 ms-0"
              href="#"
              onClick={(event) => {
                event.preventDefault();
                (window as Window & { Osano?: { cm?: { showDrawer?: (dialog: string) => void } } }).Osano?.cm?.showDrawer?.('osano-cm-dom-info-dialog-open');
              }}
            >
              Manage&nbsp;My&nbsp;Cookie&nbsp;Preferences
            </a>
            <span className="d-md-inline d-none">&nbsp;|&nbsp;</span>
            <br className="d-lg-none" />
            <a
              className="lh-lg py-1 ms-0"
              href="#"
              onClick={(event) => {
                event.preventDefault();
                (window as Window & { Osano?: { cm?: { showDoNotSell?: () => void } } }).Osano?.cm?.showDoNotSell?.();
              }}
            >
              Do&nbsp;Not&nbsp;Sell&nbsp;My&nbsp;Information
            </a>
            <br className="d-lg-none" />
          </div>
        </div>
      </footer>

      {/* Terms of Use modal — mirrors the #tos modal in .old horizontal.blade.php */}
      {tosOpen && (
        <div
          className="modal fade show d-block"
          style={{ background: 'rgba(0,0,0,0.5)' }}
          role="dialog"
          tabIndex={-1}
          onClick={(e) => {
            if (e.target === e.currentTarget) setTosOpen(false);
          }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content border" style={{ borderRadius: 20 }}>
              <div className="modal-header border-bottom-0">
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={() => setTosOpen(false)}
                />
              </div>
              <div className="modal-body pt-0">
                <div className="row px-2 mx-2 justify-content-center">
                  <div className="col-12 p-0 m-0">
                    <h1 className="text-center mt-0">Terms of Use</h1>
                  </div>
                  <div className="col-12 p-0 m-0">
                    Advanced Drainage Systems, Inc. (“ADS”) is not responsible for the accuracy,
                    appropriateness, or suitability of any product selected or purchased by the user.
                    ADS recommends that any designs, plans, or project specifications be reviewed by a
                    professional engineer, including any design engineer of record
                    <br />
                    <br />
                    The user is solely responsible for determining the suitability of any product for
                    its intended use and its compliance with any design plans, specifications, and/or
                    applicable laws, and the user assumes all risk and liability whatsoever in
                    connection therewith. ADS shall not be liable for any special, indirect, or
                    consequential damages incurred by the user or any third party.
                    <br />
                    <br />
                    All orders are subject to ADS’ Terms and Conditions of Sales:
                    <br />
                    <a
                      href="https://www.adspipe.com/terms-conditions/terms-and-conditions-sales"
                      target="_blank"
                      rel="noreferrer"
                    >
                      https://www.adspipe.com/terms-conditions/terms-and-conditions-sales
                    </a>
                    .
                    <br />
                    Contact ADS for any questions. By using the FlexStorm Project Planner, user hereby
                    accepts these Terms&nbsp;of&nbsp;Use.
                    <br />
                    <p className="text-end">Last Updated: 06/24/2025</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
